import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";
import { catchError, finalize, map, type Observable, of, shareReplay, tap } from "rxjs";

import { DEFAULT_LOCALE } from "@/app/i18n.config";

@Injectable({
  providedIn: "root",
})
/**
 * Service responsible for dynamically loading translation files
 * and updating the translation state for the current language.
 */
export class LoadTranslationService {
  private translateService = inject(TranslateService);
  private http = inject(HttpClient);

  private loadedSections: Set<string> = new Set();
  private pendingRequests: Map<string, Observable<boolean>> = new Map();

  /**
   * Loads translation for the given section and current language.
   * Results are cached to prevent duplicate HTTP requests.
   *
   * @param {string} section - The name of the translation section to load.
   * @returns {Observable<boolean>} An observable that emits true if the translations
   *          are successfully loaded, or false if an error occurs.
   */
  loadTranslations(section: string): Observable<boolean> {
    const lang =
      this.translateService.getCurrentLang() ||
      this.translateService.getFallbackLang() ||
      DEFAULT_LOCALE.language;
    const sectionKey = `${section}-${lang}`;

    if (this.loadedSections.has(sectionKey)) {
      return of(true);
    }

    const existingRequest = this.pendingRequests.get(sectionKey);
    if (existingRequest) {
      return existingRequest;
    }

    const obs$ = this.http
      .get<Record<string, string>>(`./i18n/${section}/${lang}.json`, {
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      })
      .pipe(
        tap((translations) => {
          this.translateService.setTranslation(lang, translations, true);
        }),
        map(() => true),
        tap(() => this.loadedSections.add(sectionKey)),
        catchError((error) => {
          console.error("Translations loading error:", error);
          return of(false);
        }),
        finalize(() => this.pendingRequests.delete(sectionKey)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.pendingRequests.set(sectionKey, obs$);
    return obs$;
  }
}
